/**
 * O avisador da mensagem nova do chat — D2 (passo 14, 07/10/2026), spec §7. Fora do React e
 * testável sem componente; o `NotificationContext` o chama no `chat:mensagem_nova`.
 *
 * O servidor decide o nível de cada pessoa para ESTA mensagem (`aviso_da_mensagem`) e o manda
 * no evento como `aviso`: `som` → aviso na tela + som; `aviso` → só o aviso; `sino`/`contador`
 * → nada na tela (só o número). Quem cala o aviso aqui, nesta ordem:
 *   1. a conversa está à vista (aberta e aba visível): a pessoa está lendo;
 *   2. o nível não é `som`/`aviso`;
 *   3. (só o som) interruptor do aparelho, esta aba toca, a conversa não tocou há 30 s e a
 *      janela global de 5 s está livre.
 *
 * ⚠️ A conversa só "gasta" a janela de 30 s quando o som SAI. A aba que não toca e a janela
 *    global ocupada não gastam: senão a aba errada, ou o som de outra conversa, calaria esta
 *    por meio minuto sem ninguém ter ouvido nada.
 */
import { tocarSomDeAviso } from "@/lib/somDeAviso";
import { reservarJanelaDoSom } from "./janelaDoSom";
import { salaAbertaAVista } from "./salaAberta";
import { somDoAparelhoLigado } from "./somDoAparelho";
import { contarAvisoNoTitulo } from "./tituloDaAba";
import { estaAbaToca } from "./travaDoSom";

/** O `data` do evento `chat:mensagem_nova` (aninhado como o do `notification:new`). */
export interface MensagemNova {
  user_id?: string;
  room_id: string;
  room_type: string;
  sala: string;
  author_id: string;
  author_name: string;
  preview: string;
  message_id: string;
  created_at?: string;
  mencionou_voce?: boolean;
  silenciada?: boolean;
  aviso?: string | null;
}

/** No máximo 1 som por conversa a cada 30 s: uma rajada de mensagens toca uma vez. */
export const JANELA_DA_CONVERSA_MS = 30_000;

/** As salas em que a mensagem avisa (as mesmas do backend). Comentário do Drive e voz seguem de fora. */
export const SALAS_DO_CHAT: ReadonlySet<string> = new Set(["DM", "GROUP", "SECTOR_AUTO"]);

const EVENTOS_DE_MENCAO = new Set(["chat.mention", "chat.sector_mention"]);

/**
 * A linha de menção do sino anuncia na tela? Nas conversas do chat, não: a menção sempre chega
 * ANTES da mensagem (o parser roda antes do aviso) e calaria o nível maior da mensagem, e o push
 * sairia em dobro. Quem anuncia (tela, som e push) é o `chat:mensagem_nova`, que já traz
 * `mencionou_voce` e o MAIOR nível (spec §7.1 item 2).
 *
 * `dados` é o `data` DA LINHA de notificação (o `item.data` do contexto, um nível abaixo do
 * payload do `notification:new`), onde o `chat_mention_parser` grava o `room_type`. Sem ele
 * (linha antiga) ou em comentário do Drive (`ENTITY_COMMENTS`), que não tem mensagem: a menção
 * anuncia, como no D1.
 */
export function mencaoAnunciaNaTela(eventType: string | null | undefined, dados: unknown): boolean {
  if (!eventType || !EVENTOS_DE_MENCAO.has(eventType)) return true;
  if (typeof dados !== "object" || dados === null) return true;
  const tipo = (dados as { room_type?: unknown }).room_type;
  if (typeof tipo !== "string") return true;
  // O backend grava em maiúsculas; a caixa não deve decidir se a mensagem anuncia em dobro.
  return !SALAS_DO_CHAT.has(tipo.toUpperCase());
}

/** Quando saiu o último som de cada conversa (memória desta aba; some no recarregar). */
const ultimoSomPorConversa = new Map<string, number>();

function conversaLivre(roomId: string, agora: number): boolean {
  const ultimo = ultimoSomPorConversa.get(roomId);
  if (ultimo === undefined) return true;
  // Marca no futuro (relógio mexido para trás) não pode calar a conversa até o relógio voltar.
  if (ultimo > agora) return true;
  return agora - ultimo >= JANELA_DA_CONVERSA_MS;
}

/**
 * Decide, mostra (pela função recebida), conta no título e toca — nesta ordem.
 * `mostrou` só é verdadeiro se o aviso na tela de fato saiu; `tocou`, se o som saiu.
 */
export function anunciarMensagem(
  m: MensagemNova,
  mostrar: () => void,
  agora: number = Date.now()
): { mostrou: boolean; tocou: boolean } {
  const resultado = { mostrou: false, tocou: false };
  // A conversa que a pessoa está lendo não avisa nem toca: a mensagem já está na tela dela.
  if (salaAbertaAVista(m.room_id)) return resultado;
  if (m.aviso !== "som" && m.aviso !== "aviso") return resultado;

  try {
    mostrar();
    resultado.mostrou = true;
    contarAvisoNoTitulo();
  } catch (erro) {
    // O aviso na tela que falha não cala o som (o contador da lista já contou).
    console.warn("[avisos] o aviso da mensagem na tela falhou:", erro);
  }

  // ⚠️ A ordem importa: cada condição barata e sem efeito colateral vem antes de
  //    `reservarJanelaDoSom`, que grava a janela global; e a conversa só é marcada depois dela.
  if (
    m.aviso === "som" &&
    somDoAparelhoLigado() &&
    estaAbaToca() &&
    conversaLivre(m.room_id, agora) &&
    reservarJanelaDoSom()
  ) {
    // `tocarSomDeAviso` devolve se agendou o som (áudio ainda bloqueado → `false`): a conversa só
    // gasta a janela de 30 s quando o som SAI, então ela segue livre para a próxima mensagem.
    // ⚠️ Isso não devolve a janela GLOBAL de 5 s: `reservarJanelaDoSom` já a gravou, e a próxima
    // mensagem só pode tocar depois dela.
    resultado.tocou = tocarSomDeAviso("aviso");
    if (resultado.tocou) ultimoSomPorConversa.set(m.room_id, agora);
  }
  return resultado;
}

/** Só para os testes. */
export function _reiniciarAvisoDaMensagemParaTeste(): void {
  ultimoSomPorConversa.clear();
}
